import { DEFAULT_ROLE_PERMISSIONS } from './role-permissions';

import type { Permission } from './permission';
import type { PlatformRole } from '../identity/role';

/**
 * Administrator overrides from the feature-flag grid. Strictly subtractive:
 * an entry can hide an action the base matrix grants, but can never grant one
 * it withholds. Escalation is therefore impossible by construction.
 */
export type FeatureFlagOverrides = Readonly<Partial<Record<PlatformRole, readonly Permission[]>>>;

/** Platform Admin is immune to overrides so the platform can never be locked out. */
const IMMUNE_ROLE: PlatformRole = 'platform_admin';

export function computeEffectivePermissions(
  role: PlatformRole,
  overrides: FeatureFlagOverrides = {},
  matrix: Readonly<Record<PlatformRole, readonly Permission[]>> = DEFAULT_ROLE_PERMISSIONS,
): ReadonlySet<Permission> {
  const granted = new Set<Permission>(matrix[role]);
  if (role === IMMUNE_ROLE) return granted;

  for (const revoked of overrides[role] ?? []) granted.delete(revoked);
  return granted;
}

/** A read model of what the current session may do. */
export interface AccessContext {
  readonly role: PlatformRole;
  readonly permissions: ReadonlySet<Permission>;
}

export function createAccessContext(
  role: PlatformRole,
  permissions: Iterable<Permission>,
): AccessContext {
  return { role, permissions: new Set(permissions) };
}

export function can(ctx: AccessContext | null, permission: Permission): boolean {
  return ctx?.permissions.has(permission) ?? false;
}

export function canEvery(ctx: AccessContext | null, permissions: readonly Permission[]): boolean {
  return permissions.every((p) => can(ctx, p));
}

export function canSome(ctx: AccessContext | null, permissions: readonly Permission[]): boolean {
  return permissions.some((p) => can(ctx, p));
}

/**
 * True when the session may only see projects it is assigned to.
 * Drives the server-side project filter and the empty state shown to a
 * consultant with no assignments yet.
 */
export function isRestrictedToAssignedProjects(ctx: AccessContext | null): boolean {
  return !can(ctx, 'project.viewAll');
}
